/**
 * StaffThisJobView.tsx
 * The "Staff this job" v2 hero (STAFF-VIEW-V2.md) — rebuilt TASK-FIRST and
 * SKILL-AWARE. This view's job is staff selection, so tasks lead, skill is on
 * every assignment, and capacity is a readable week-by-week monitor. Layout
 * top→bottom (BUG-2026-0144 — Gigi's feedback: tasks first, then capacity):
 *   1. Job header (source + span + machines-chosen) — reused from v1.
 *   2. Task staffing cards — one per task: the skill it needs and who's on it
 *      (with a qualified/needs-mentor badge each), a mentor nudge, and a
 *      skill-smart ＋Assign picker. WHO + SKILL only — no % here (Mike simplify).
 *      Unstaffed tasks read amber.
 *   3. Roster timeline (the capacity hero) — people×weeks: each person's row
 *      shows their assigned tasks as faint context + ONE editable blanket % per
 *      week (default 100, over-100 nags), plus a light total-load caption.
 *   4. Legend + a link back to the legacy Classic view.
 *
 * All data + writes flow through ONE useStaffJobModel (one roster + one capacity
 * fetch + one skill snapshot) so the cards and the rail never drift. Colors via
 * theme tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  Link,
  Snackbar,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTheme } from '@mui/material/styles';

import { StaffRosterTimeline } from './StaffRosterTimeline';
import { TaskStaffingTable } from './TaskStaffingTable';
import { IndexStaffingTableRows } from './IndexStaffingTableRows';
import {
  listIndexAllocations,
  listIndexAllocationAssignees,
  type IndexAllocation,
  type IndexAllocationAssignee,
} from '../../../../services/scheduleIndex';
import { StaffSectionLabel, staffHair } from './staffViewStyle';
import { FUTURE_HORIZON_WEEKS } from './staffCapacityModel';
import { StaffLegend } from './StaffLegend';
import { useStaffJobModel } from './useStaffJobModel';
import { useGraphPhotos } from '../../../../hooks/useGraphPhotos';
import type { Scenario } from '../../../../services/scheduleScenario';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

export interface StaffThisJobViewProps {
  scenario: Scenario;
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  scenarioKey: string | null;
  tasks: TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  /** Plan is Submitted/Archived → read-only. */
  locked: boolean;
  onAllocationChanged: () => void;
  /** Reload one task's assignees after a presence write. */
  onTaskPresenceChanged: (taskKey: string) => void;
  capacityRefreshKey: number;
  holidays: readonly string[];
  hoveredWeek: string | null;
  onHoverWeek: (weekStart: string | null) => void;
  /** Switch back to the legacy Classic view (the task-by-task machine schedule). */
  onSwitchToClassic: () => void;
}

/** Tasks sorted by run-start then name, so the card list reads chronologically. */
function sortedTasks(tasks: TaskWithBookings[]): TaskWithBookings[] {
  return [...tasks].sort((a, b) => {
    const sa = a.StartDate ?? '';
    const sb = b.StartDate ?? '';
    if (sa !== sb) return sa.localeCompare(sb);
    return a.Name.localeCompare(b.Name);
  });
}

export const StaffThisJobView: React.FC<StaffThisJobViewProps> = ({
  source,
  sourceKey,
  scenarioKey,
  tasks,
  assignmentMap,
  locked,
  onAllocationChanged,
  onTaskPresenceChanged,
  capacityRefreshKey,
  hoveredWeek,
  onHoverWeek,
  onSwitchToClassic,
}) => {
  const theme = useTheme();

  // Index tests inside Section 1 (2026-07-28): the source's pooled allocations
  // render as extra rows of the SAME table, under an "Index tests" divider —
  // no more bottom-of-page section in this view. Refreshed alongside capacity
  // (a window edit in Phase 2 or a generate re-pools them). Declared BEFORE the
  // model so their windows can extend the project window (2026-07-29).
  const [indexAllocations, setIndexAllocations] = useState<IndexAllocation[]>([]);
  const reloadIndexAllocations = useCallback(() => {
    listIndexAllocations(scenarioKey ?? 'BASELINE', { sourceType: source, sourceKey })
      .then(setIndexAllocations)
      .catch(() => setIndexAllocations([]));
  }, [scenarioKey, source, sourceKey]);
  useEffect(() => {
    reloadIndexAllocations();
  // capacityRefreshKey: cheap cross-panel signal that staffing/pooling changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadIndexAllocations, capacityRefreshKey]);

  // Per-allocation assignees — powers the Team-capacity pinned "(index)" rows.
  // (The table rows fetch their own copy; this map is the timeline's view.)
  const [indexAssignees, setIndexAssignees] = useState<Record<string, IndexAllocationAssignee[]>>({});
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out: Record<string, IndexAllocationAssignee[]> = {};
      await Promise.all(indexAllocations.map(async (a) => {
        try {
          out[a.IndexAllocationKey] = await listIndexAllocationAssignees(a.IndexAllocationKey);
        } catch {
          out[a.IndexAllocationKey] = [];
        }
      }));
      if (!cancelled) setIndexAssignees(out);
    })();
    return () => { cancelled = true; };
  // capacityRefreshKey re-fires after any staffing change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexAllocations, capacityRefreshKey]);

  // P7 (2026-07-29): extendable read-only outlook — the "Beyond project" pill's
  // ▶ pager grows the horizon in 4-week steps (◀ shrinks back to the default 5).
  const [futureHorizonWeeks, setFutureHorizonWeeks] = useState(FUTURE_HORIZON_WEEKS);

  const model = useStaffJobModel({
    source,
    sourceKey,
    scenarioKey,
    tasks,
    assignmentMap,
    locked,
    onTaskPresenceChanged,
    onAllocationChanged,
    capacityRefreshKey,
    indexAllocations,
    futureHorizonWeeks,
  });

  const cards = sortedTasks(tasks);
  const unstaffedCount = cards.filter((t) => (assignmentMap[t.ScheduleTaskKey] ?? []).length === 0).length;

  // Real MS Graph profile photos for everyone on the job (same source the Board
  // grid uses) so the task-card avatars show faces, not initials. Best-effort +
  // cached; falls back to colored initials when a photo isn't available.
  const assigneeEmails = useMemo(() => {
    const s = new Set<string>();
    for (const rows of Object.values(assignmentMap)) {
      for (const a of rows) if (a.email) s.add(a.email);
    }
    // Roster-only members (e.g. index-test staffing, 2026-07-28) aren't in the
    // task assignmentMap — their emails ride the roster entries instead.
    for (const e of model.employeeRows) if (e.email) s.add(e.email);
    return [...s];
  }, [assignmentMap, model.employeeRows]);
  const photoByEmail = useGraphPhotos(assigneeEmails);

  // userKey → resolved photo, so the Team column + breakdown popover can show the
  // same face the task table does (the roster rows are keyed by userKey, not email).
  const photoByUserKey = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const rows of Object.values(assignmentMap)) {
      for (const a of rows) {
        const lk = a.UserKey.toLowerCase();
        const url = (a.email ? photoByEmail.get(a.email) : null) ?? a.photoUrl ?? null;
        if (!m.has(lk) || (url && !m.get(lk))) m.set(lk, url);
      }
    }
    for (const e of model.employeeRows) {
      const lk = e.userKey.toLowerCase();
      const url = e.email ? (photoByEmail.get(e.email) ?? null) : null;
      if (!m.has(lk) || (url && !m.get(lk))) m.set(lk, url);
    }
    return m;
  }, [assignmentMap, model.employeeRows, photoByEmail]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* StaffJobHeader band removed 2026-07-26 (item 1) — the header chrome
          already names the job; the band only repeated it and the machine list. */}

      {/* ── Section 1 — "Tests on this job" compact table (redesign 2026-07-14). ── */}
      <StaffSectionLabel
        n={1}
        title="Tests on this job"
        hint="— who's assigned to each test"
        trailing={unstaffedCount > 0 ? (
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: theme.schedule.maintenance }}>
            {unstaffedCount} unstaffed
          </Typography>
        ) : undefined}
      />

      {cards.length === 0 && indexAllocations.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
          No tasks on this job yet.
        </Typography>
      ) : (
        <TaskStaffingTable
          tasks={cards}
          assignmentMap={assignmentMap}
          model={model}
          locked={locked}
          photoByEmail={photoByEmail}
          extraRows={
            <IndexStaffingTableRows
              allocations={indexAllocations}
              model={model}
              locked={locked}
              photoByEmail={photoByEmail}
              onChanged={onAllocationChanged}
              // BUG-2026-0174: a Team-capacity roster add staffs people on the
              // index allocations server-side — reload the chips on the same
              // refresh signal every other panel uses.
              refreshKey={capacityRefreshKey}
            />
          }
        />
      )}

      {/* Visible separator + buffer between the two sections (2026-07-14 feedback). */}
      <Divider sx={{ borderColor: staffHair(theme), mt: 1, mb: 0.5 }} />

      {/* ── Section 2 — "Team capacity" split availability bar grid (redesign
          2026-07-14). Each person's row shows a split committed/uncommitted/
          this-project bar per week, editable within the project window and
          read-only for the future availability horizon. ── */}
      <StaffSectionLabel
        n={2}
        title="Team capacity"
        hint="— this project's share of each person's week, and where they're booked elsewhere"
      />
      <StaffRosterTimeline
        model={model}
        tasks={tasks}
        assignmentMap={assignmentMap}
        indexAllocations={indexAllocations}
        indexAssigneesByAllocation={indexAssignees}
        editable={!locked}
        hoveredWeek={hoveredWeek}
        onHoverWeek={onHoverWeek}
        source={source}
        sourceKey={sourceKey}
        scenarioKey={scenarioKey}
        photoByUserKey={photoByUserKey}
        weekHoverHighlight={false}
        futureHorizonWeeks={futureHorizonWeeks}
        onFutureHorizonChange={setFutureHorizonWeeks}
      />

      <StaffLegend />

      {/* Precision on demand — the deep task-by-task machine view lives in Classic. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.25 }}>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Need the task-by-task machine schedule?
        </Typography>
        <Link
          component="button"
          type="button"
          onClick={onSwitchToClassic}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.25,
            fontSize: '0.72rem', fontWeight: 700,
            color: theme.schedule.employeeAlloc, textDecorationColor: theme.schedule.employeeAlloc,
          }}
        >
          Switch to Classic
          <ArrowForwardIcon sx={{ fontSize: 13 }} />
        </Link>
      </Box>

      {/* Task-write error (day-off collision / lock / network) — verbatim. */}
      <Snackbar
        open={Boolean(model.error)}
        autoHideDuration={7000}
        onClose={model.clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={model.clearError} sx={{ width: '100%' }}>
          {model.error}
        </Alert>
      </Snackbar>

      {/* Roster (%/add/remove) mutation error — verbatim. */}
      <Snackbar
        open={Boolean(model.rosterMutationError)}
        autoHideDuration={7000}
        onClose={model.clearRosterMutationError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={model.clearRosterMutationError} sx={{ width: '100%' }}>
          {model.rosterMutationError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
